import type {
  OrderFinancialVO,
  PolicyholderVO,
} from "../OrderForms/orders";

export type ZuuValidationError = {
  name: string[];
  errors: string[];
};

function toNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

export function calculateAgeFromBirthDate(
  birthDate: string | undefined,
  now: Date = new Date(),
): number | null {
  if (!birthDate) return null;

  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;

  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function parseRetirementAge(value: string | undefined): number | null {
  if (!value) return null;
  const matched = value.match(/\d+/);
  if (!matched) return null;
  const age = Number.parseInt(matched[0], 10);
  return Number.isFinite(age) ? age : null;
}

export function shouldShowZuuRetirementFields(
  policyholder: PolicyholderVO | undefined,
  now: Date = new Date(),
): boolean {
  const work = policyholder?.work;
  const intendedRetirementAge = work?.intendedRetirementAge;

  if (!intendedRetirementAge) {
    return false;
  }

  if (intendedRetirementAge === "RETIRED") {
    return false;
  }

  const currentAge = calculateAgeFromBirthDate(policyholder?.basic?.birthDate, now);
  if (currentAge == null) {
    return intendedRetirementAge === "OTHERS"
      ? parseRetirementAge(work?.intendedRetirementAgeOther) != null
      : parseRetirementAge(intendedRetirementAge) != null;
  }

  if (intendedRetirementAge === "OTHERS") {
    const otherAge = parseRetirementAge(work?.intendedRetirementAgeOther);
    return otherAge != null ? otherAge > currentAge : false;
  }

  const plannedAge = parseRetirementAge(intendedRetirementAge);
  return plannedAge != null ? plannedAge > currentAge : false;
}

export function isStrictPositiveInteger(value: unknown): boolean {
  const numericValue = toNumber(value);
  return numericValue != null && Number.isInteger(numericValue) && numericValue >= 0;
}

export function isPositiveNumberWithMaxTwoDecimals(value: unknown): boolean {
  const numericValue = toNumber(value);
  if (numericValue == null || numericValue < 0) {
    return false;
  }
  return Number.isInteger(numericValue * 100);
}

export function isStrictPositiveNumberWithMaxTwoDecimals(
  value: unknown,
): boolean {
  const numericValue = toNumber(value);
  if (numericValue == null || numericValue <= 0) {
    return false;
  }
  return Number.isInteger(numericValue * 100);
}

export function validateZuuFinancialInfoStep2(
  data: OrderFinancialVO,
  options: {
    isFinancialInfoRequired?: boolean;
    showRetirementFields: boolean;
  },
): ZuuValidationError[] {
  if (!options.isFinancialInfoRequired) {
    return [];
  }

  const errors: ZuuValidationError[] = [];

  if (!isStrictPositiveInteger(data.income?.avgMonthlyIncome)) {
    errors.push({
      name: ["income", "avgMonthlyIncome"],
      errors: ["請輸入非負整數"],
    });
  }
  if (!isStrictPositiveInteger(data.expense?.avgMonthlyExpense)) {
    errors.push({
      name: ["expense", "avgMonthlyExpense"],
      errors: ["請輸入非負整數"],
    });
  }
  if (!isStrictPositiveInteger(data.currentAssets?.currentLiquidAssets)) {
    errors.push({
      name: ["currentAssets", "currentLiquidAssets"],
      errors: ["請輸入非負整數"],
    });
  }

  if (options.showRetirementFields) {
    if (!isStrictPositiveInteger(data.income?.expectedRetireIncome)) {
      errors.push({
        name: ["income", "expectedRetireIncome"],
        errors: ["請輸入非負整數"],
      });
    }
    if (!isStrictPositiveInteger(data.currentAssets?.expectedRetireLiquidAssets)) {
      errors.push({
        name: ["currentAssets", "expectedRetireLiquidAssets"],
        errors: ["請輸入非負整數"],
      });
    }
  }

  return errors;
}

export function validateZuuPremiumFinancing(
  data: OrderFinancialVO,
): ZuuValidationError[] {
  if (data.isPremiumFinanced !== true) {
    return [];
  }

  const errors: ZuuValidationError[] = [];
  const repaymentSources = data.premiumFinancingRepaymentSources || [];
  const firstPremium = (data.premiumFinancing || [])[0] || {};

  if (repaymentSources.length === 0) {
    errors.push({
      name: ["premiumFinancingRepaymentSource"],
      errors: ["請選擇還款來源"],
    });
  }
  if (repaymentSources.includes("D") && !data.premiumFinancingRepaymentSourceOther?.trim()) {
    errors.push({
      name: ["premiumFinancingRepaymentSourceOther"],
      errors: ["請輸入其他還款來源"],
    });
  }

  if (!isPositiveNumberWithMaxTwoDecimals(data.outstandingMortgageLoanAmount)) {
    errors.push({
      name: ["outstandingMortgageLoanAmount"],
      errors: ["請輸入大於或等於 0 的金額，最多 2 位小數"],
    });
  }
  if (!isPositiveNumberWithMaxTwoDecimals(data.outstandingOtherLoanAmount)) {
    errors.push({
      name: ["outstandingOtherLoanAmount"],
      errors: ["請輸入大於或等於 0 的金額，最多 2 位小數"],
    });
  }

  if (typeof data.premiumFinancingEstimateConsent !== "boolean") {
    errors.push({
      name: ["premiumFinancingEstimateConsent"],
      errors: ["請選擇是否願意提供保費融資資料"],
    });
  }

  if (data.premiumFinancingEstimateConsent === true) {
    if (!firstPremium.lenderName?.trim()) {
      errors.push({
        name: ["premiumFinancingLenderName"],
        errors: ["請輸入貸方名稱"],
      });
    }
    if (!isStrictPositiveNumberWithMaxTwoDecimals(firstPremium.loanAmount)) {
      errors.push({
        name: ["premiumFinancingLoanAmount"],
        errors: ["請輸入大於 0 的金額，最多 2 位小數"],
      });
    }
    if (!firstPremium.loanInterestRate?.trim()) {
      errors.push({
        name: ["premiumFinancingLoanInterestRate"],
        errors: ["請輸入貸款利率"],
      });
    }
    if (!firstPremium.loanTerm?.trim()) {
      errors.push({
        name: ["premiumFinancingLoanTerm"],
        errors: ["請輸入貸款期"],
      });
    }
    if (!firstPremium.repaymentAmount?.trim()) {
      errors.push({
        name: ["premiumFinancingRepaymentAmount"],
        errors: ["請輸入每期還款金額"],
      });
    }
  }

  if (data.premiumFinancingImportantDisclosureConfirmed !== true) {
    errors.push({
      name: ["premiumFinancingImportantDisclosureConfirmed"],
      errors: ["請確認已閱讀並理解重要說明及影響"],
    });
  }
  if (data.premiumFinancingRepaymentConfirmed !== true) {
    errors.push({
      name: ["premiumFinancingRepaymentConfirmed"],
      errors: ["請確認有足夠資金償還貸款"],
    });
  }

  return errors;
}
